import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import { useDateFormat } from '@/hooks/useDateFormat';
import type {
  WeightProductionEntryRegisterRow,
  WeightProductionPendingJobCompletionRow,
  WeightProductionPlanRegisterRow,
} from '@/types/reports';
import { withId } from './reportHelpers';

const TABS = ['weightProductionPlanRegister', 'weightProductionPendingJobCompletion', 'weightProductionEntryRegister'] as const;

/** Weigh & Label registers. The nav leaf pointing here is gated on
 * ProcessConfiguration.WeightProductionEnabled, mirroring Job Work's own module-gated report. */
export default function WeightProductionReportsPage() {
  const { t } = useTranslation('reports');
  const { formatDate, formatDateTime } = useDateFormat();
  const [tab, setTab] = useState(0);

  const { data: plans = [], isLoading: loadingPlans } = useQuery({
    queryKey: ['reports', 'weight-production-plan-register'],
    queryFn: async () => (await apiClient.get<WeightProductionPlanRegisterRow[]>('/reports/weight-production-plan-register')).data,
    enabled: tab === 0,
  });
  const { data: pending = [], isLoading: loadingPending } = useQuery({
    queryKey: ['reports', 'weight-production-pending-job-completion'],
    queryFn: async () => (await apiClient.get<WeightProductionPendingJobCompletionRow[]>('/reports/weight-production-pending-job-completion')).data,
    enabled: tab === 1,
  });
  const { data: entries = [], isLoading: loadingEntries } = useQuery({
    queryKey: ['reports', 'weight-production-entry-register'],
    queryFn: async () => (await apiClient.get<WeightProductionEntryRegisterRow[]>('/reports/weight-production-entry-register')).data,
    enabled: tab === 2,
  });

  const planColumns: DataTableColumn<ReturnType<typeof withId<WeightProductionPlanRegisterRow>>[number]>[] = [
    { field: 'orderNumber', headerName: t('columns.number') },
    { field: 'materialCode', headerName: t('columns.material'), render: (r) => `${r.materialCode} — ${r.materialName}` },
    { field: 'warehouseName', headerName: t('columns.warehouse'), filterable: true },
    { field: 'sourceMode', headerName: t('columns.sourceMode'), filterable: true },
    { field: 'planBasis', headerName: t('columns.planBasis'), filterable: true },
    { field: 'plannedQty', headerName: t('columns.plannedQty'), align: 'right' },
    { field: 'producedQty', headerName: t('columns.producedQty'), align: 'right' },
    { field: 'plannedStartDate', headerName: t('columns.date'), render: (r) => (r.plannedStartDate ? formatDate(r.plannedStartDate) : '—') },
    { field: 'status', headerName: t('columns.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
  ];

  const pendingColumns: DataTableColumn<ReturnType<typeof withId<WeightProductionPendingJobCompletionRow>>[number]>[] = [
    { field: 'barcodeNo', headerName: t('columns.barcode') },
    { field: 'orderNumber', headerName: t('columns.number'), render: (r) => r.orderNumber ?? '—', filterable: true },
    { field: 'materialCode', headerName: t('columns.material'), render: (r) => `${r.materialCode} — ${r.materialName}` },
    { field: 'warehouseName', headerName: t('columns.warehouse'), filterable: true },
    { field: 'qty', headerName: t('columns.qty'), align: 'right' },
    { field: 'actualWeight', headerName: t('columns.actualWeight'), align: 'right', render: (r) => `${r.actualWeight} ${r.weightUom}` },
    { field: 'batchNo', headerName: t('columns.batchNo'), render: (r) => r.batchNo ?? '—' },
    {
      field: 'ageInDays', headerName: t('columns.age'), align: 'right',
      // The reason this register exists — a weighing sitting captured-but-unconfirmed for days is
      // stock that's real on the shop floor but invisible everywhere else in the system.
      render: (r) => (r.ageInDays >= 1
        ? <Chip size="small" color="warning" label={t('columns.days', { days: r.ageInDays })} />
        : t('columns.days', { days: r.ageInDays })),
    },
  ];

  const entryColumns: DataTableColumn<ReturnType<typeof withId<WeightProductionEntryRegisterRow>>[number]>[] = [
    { field: 'barcodeNo', headerName: t('columns.barcode') },
    { field: 'orderNumber', headerName: t('columns.number'), render: (r) => r.orderNumber ?? '—', filterable: true },
    { field: 'materialCode', headerName: t('columns.material'), render: (r) => `${r.materialCode} — ${r.materialName}` },
    { field: 'warehouseName', headerName: t('columns.warehouse'), filterable: true },
    { field: 'qty', headerName: t('columns.qty'), align: 'right' },
    { field: 'actualWeight', headerName: t('columns.actualWeight'), align: 'right', render: (r) => `${r.actualWeight} ${r.weightUom}` },
    { field: 'batchNo', headerName: t('columns.batchNo'), render: (r) => r.batchNo ?? '—' },
    { field: 'captureMode', headerName: t('columns.captureMode'), filterable: true },
    { field: 'operatorName', headerName: t('columns.operator'), render: (r) => r.operatorName ?? '—' },
    { field: 'createdDate', headerName: t('columns.dateTime'), render: (r) => formatDateTime(r.createdDate) },
    {
      field: 'stockPosted', headerName: t('columns.stockStatus'),
      render: (r) => <StatusChip status={r.status === 'Cancelled' ? 'Cancelled' : r.stockPosted ? 'Posted' : 'Pending'} />,
      filterable: true,
    },
  ];

  return (
    <>
      <PageHeader title={t('modules.weightProduction.title')} subtitle={t('modules.weightProduction.subtitle')} />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        {TABS.map((key) => (
          <Tab key={key} label={t(`${key}.title`)} />
        ))}
      </Tabs>

      <Box hidden={tab !== 0}>
        <DataTable columns={planColumns} rows={withId(plans, (r) => `${r.orderId}-${r.materialId}`)} loading={loadingPlans} searchable exportFileName="weight-production-plan-register" />
      </Box>
      <Box hidden={tab !== 1}>
        <DataTable columns={pendingColumns} rows={withId(pending, (r) => r.entryId)} loading={loadingPending} searchable exportFileName="weight-production-pending-job-completion" />
      </Box>
      <Box hidden={tab !== 2}>
        <DataTable columns={entryColumns} rows={withId(entries, (r) => r.entryId)} loading={loadingEntries} searchable exportFileName="weight-production-entry-register" />
      </Box>
    </>
  );
}
